import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import userflow from 'userflow.js';
import { useAuth } from './auth';

interface UserflowContextType {
  /** Programmatically start a Userflow flow/checklist/launcher by content ID */
  startContent: (contentId: string) => void;
  /** Track a custom event (for triggering flows based on user behavior) */
  trackEvent: (eventName: string, attributes?: Record<string, unknown>) => void;
  /** Whether Userflow has been initialized */
  isReady: boolean;
}

const UserflowContext = createContext<UserflowContextType | undefined>(undefined);

export const UserflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const isInitialized = useRef(false);

  // Initialize Userflow once
  useEffect(() => {
    const token = import.meta.env.VITE_USERFLOW_TOKEN;
    if (!token || token === 'your_userflow_token_here') {
      if (import.meta.env.DEV) {
        console.warn('[Userflow] No VITE_USERFLOW_TOKEN set — skipping initialization');
      }
      return;
    }

    if (!isInitialized.current) {
      userflow.init(token);
      isInitialized.current = true;
      console.log('[Userflow] Initialized');
    }
  }, []);

  // Identify / reset user on auth state change
  useEffect(() => {
    if (!isInitialized.current) return;

    if (currentUser) {
      userflow.identify(currentUser.id, {
        name: currentUser.name || currentUser.email || '',
        email: currentUser.email || '',
        is_admin: currentUser.isAdmin ?? false,
      });
      console.log('[Userflow] User identified:', currentUser.id);
    } else {
      userflow.reset();
      console.log('[Userflow] User reset (logged out)');
    }
  }, [currentUser]);

  const startContent = useCallback((contentId: string) => {
    if (!isInitialized.current) {
      console.warn('[Userflow] Not initialized — cannot start content');
      return;
    }
    userflow.start(contentId);
  }, []);

  const trackEvent = useCallback((eventName: string, attributes?: Record<string, unknown>) => {
    if (!isInitialized.current) return;
    userflow.track(eventName, attributes);
  }, []);

  return (
    <UserflowContext.Provider value={{
      startContent,
      trackEvent,
      isReady: isInitialized.current,
    }}>
      {children}
    </UserflowContext.Provider>
  );
};

export const useUserflow = (): UserflowContextType => {
  const context = useContext(UserflowContext);
  if (!context) {
    throw new Error('useUserflow must be used within a UserflowProvider');
  }
  return context;
};
